import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { reinitUrl, shouldRemount, widgetScriptSource } from '@theme/eme-review-widget-core';

const WIDGET = '.jdgm-review-widget';
const MOUNTED = 'jdgm--done-setup-widget';
const STARS_LINK = 'a.rating-wrapper--eme-link';
const APP_SCRIPTS = '.shopify-app-block script:not([src]):is(:not([type]), [type="text/javascript"])';
const SCROLL_SETTLE_MS = 150;
const MAX_SCROLL_CORRECTIONS = 2;
const LEGACY_READY_INTERVAL_MS = 500;
const LEGACY_SETUP = ['initializeWidgets', '_customizeReviewWidget', '_renderAndSetupReviewForm', '_setupLoadReviewsEventsFor', '_setupFormsSubmit'];

class EmeReviewWidget extends Component {
  #controller = null;
  #scrollController = null;
  #settleTimer = 0;
  #legacyTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener('click', this.#handleClick, { signal });
    this.#linkStars();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#stopScrollCorrection();
    this.#stopLegacyWait();
  }

  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const previousId = this.#widgetId();
    try {
      await event.promise;
    } catch {
      return;
    }
    const live = this.isConnected ? this : document.querySelector('eme-review-widget');
    if (!(live instanceof EmeReviewWidget) || !live.isConnected) return;
    live.#linkStars();
    const mounted = live.#widget()?.classList.contains(MOUNTED) ?? true;
    if (mounted && !shouldRemount(previousId, live.#widgetId())) return;
    await live.#remount();
  };

  #linkStars() {
    for (const link of document.querySelectorAll(`main ${STARS_LINK}:not([href])`)) {
      link.setAttribute('href', `#${this.id}`);
      link.querySelector('[data-eme-reviews-link-label]')?.removeAttribute('hidden');
    }
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest(`a[href="#${this.id}"]`)) return;
    this.#correctScrollWhenSettled();
  };

  #correctScrollWhenSettled() {
    this.#stopScrollCorrection();
    this.#scrollController = new AbortController();
    const { signal } = this.#scrollController;
    let corrections = 0;
    let lastOffset = null;

    const check = () => {
      const target = parseFloat(getComputedStyle(this).scrollMarginBlockStart) || 0;
      const offset = this.getBoundingClientRect().top - target;
      const stuck = lastOffset !== null && Math.abs(offset - lastOffset) <= 1;
      if (Math.abs(offset) <= 1 || corrections >= MAX_SCROLL_CORRECTIONS || stuck) {
        this.#stopScrollCorrection();
        return;
      }
      corrections += 1;
      lastOffset = offset;
      this.scrollIntoView({ block: 'start' });
      settle();
    };

    const settle = () => {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = setTimeout(check, SCROLL_SETTLE_MS);
    };

    const stop = () => this.#stopScrollCorrection();
    document.addEventListener('scroll', settle, { capture: true, passive: true, signal });
    for (const type of ['wheel', 'touchstart', 'keydown']) {
      document.addEventListener(type, stop, { capture: true, passive: true, signal });
    }
    settle();
  }

  #stopScrollCorrection() {
    clearTimeout(this.#settleTimer);
    this.#scrollController?.abort();
    this.#scrollController = null;
  }

  #widget() {
    return this.closest('.shopify-section')?.querySelector(WIDGET) ?? null;
  }

  #widgetId() {
    return this.#widget()?.getAttribute('data-id') ?? '';
  }

  async #remount() {
    const section = this.closest('.shopify-section');
    const widget = this.#widget();
    if (!section || !widget) return;
    for (const script of section.querySelectorAll(APP_SCRIPTS)) {
      const fresh = document.createElement('script');
      for (const { name, value } of script.attributes) fresh.setAttribute(name, value);
      fresh.textContent = script.textContent;
      script.replaceWith(fresh);
    }
    const { jdgm } = globalThis;
    jdgm?.$?.(widget).removeData();
    const source = widgetScriptSource([...document.scripts].map((script) => script.src));
    if (source) {
      await import(reinitUrl(source, String(Date.now())));
      return;
    }
    this.#setupLegacyWidget(widget);
  }

  #setupLegacyWidget(widget) {
    const content = widget.querySelector('.jdgm-legacy-widget-content');
    if (content instanceof HTMLElement) content.style.display = '';
    widget.querySelector('.jdgm-ssr-reviews')?.remove();

    const id = widget.getAttribute('data-id');
    this.#stopLegacyWait();
    const attempt = () => {
      if (!widget.isConnected || widget.getAttribute('data-id') !== id) {
        this.#stopLegacyWait();
        return;
      }
      const { jdgm } = globalThis;
      if (!LEGACY_SETUP.every((name) => typeof jdgm?.[name] === 'function')) return;
      this.#stopLegacyWait();
      if (typeof jdgm.setupMediaGallery !== 'function') jdgm.setupMediaGallery = () => {};
      jdgm.initializeWidgets();
      const allowed = !globalThis.jdgmSettings?.disable_web_reviews && (jdgm.checkCustomerAccountRestriction?.() ?? true);
      for (const link of widget.querySelectorAll('.jdgm-write-rev-link')) link.style.display = allowed ? '' : 'none';
    };
    this.#legacyTimer = setInterval(attempt, LEGACY_READY_INTERVAL_MS);
    attempt();
  }

  #stopLegacyWait() {
    clearInterval(this.#legacyTimer);
    this.#legacyTimer = 0;
  }
}

if (!customElements.get('eme-review-widget')) customElements.define('eme-review-widget', EmeReviewWidget);
